import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { addMinutes, addHours } from 'date-fns';
import sv from 'date-fns/locale/sv';

// Constants
import { DATE_FORMAT } from '@client/definitions/api';

// Hooks
import { useField, useFormikContext } from 'formik';

// Components
import DatePicker, { registerLocale } from 'react-datepicker';
import { ErrorMessage } from '@client/components/formComponents/errorMessage/ErrorMessage';

// Styles
import styles from './datePickerRange.scss';
import 'react-datepicker/dist/react-datepicker.css';

export const DatePickerRange = ({
  startDateName,
  endDateName,
  minDate = null,
  startDateLabel,
  endDateLabel,
  endDatePlaceholder = null,
  className = '',
}) => {
  const { setFieldValue } = useFormikContext();
  const [startDate, startDateMeta] = useField({ name: startDateName });
  const [endDate, endDateMeta] = useField({ name: endDateName });
  const startDateValue = startDate?.value;
  const endDateValue = endDate?.value;
  registerLocale('sv', sv);

  const handleEndDate = (val) => {
    const endDateWithFixedTime = val ? setMidnightTime(val) : null;
    setFieldValue(endDate?.name, endDateWithFixedTime);
  };

  const setMidnightTime = (date) => {
    const endDateWithMinutes = addMinutes(date, 59);
    const endDateWithHours = addHours(endDateWithMinutes, 23);
    return endDateWithHours;
  };

  const DatePickerButton = forwardRef(({ value, onClick }, ref) => {
    const placeholder = value ? value : endDatePlaceholder;
    return (
      <button
        className={styles.button}
        onClick={onClick}
        ref={ref}
        type="button"
      >
        {placeholder}
      </button>
    );
  });

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={`${styles.pickerContainer} ${styles.withMargin}`}>
        <label className={styles.label}>{startDateLabel}</label>
        <DatePicker
          wrapperClassName={styles.datePickerStartDate}
          selected={startDateValue}
          onChange={(val) => setFieldValue(startDate?.name, val)}
          selectsStart
          dateFormat={DATE_FORMAT}
          minDate={minDate}
          startDate={startDateValue}
          endDate={endDateValue}
          locale="sv"
          withPortal
          customInput={<DatePickerButton />}
        />
        {startDateMeta?.error && (
          <ErrorMessage message={startDateMeta?.error} />
        )}
      </div>

      <div className={styles.pickerContainer}>
        <label className={styles.label}>{endDateLabel}</label>
        <DatePicker
          wrapperClassName={styles.datePickerEndDate}
          selected={endDateValue}
          onChange={(val) => handleEndDate(val)}
          selectsEnd
          startDate={startDateValue}
          endDate={endDateValue}
          dateFormat={DATE_FORMAT}
          minDate={startDateValue}
          isClearable
          locale="sv"
          withPortal
          customInput={<DatePickerButton />}
        />
        {endDateMeta?.error && <ErrorMessage message={endDateMeta?.error} />}
      </div>
    </div>
  );
};

DatePickerRange.propTypes = {
  startDateName: PropTypes.string.isRequired,
  endDateName: PropTypes.string.isRequired,
  minDate: PropTypes.instanceOf(Date),
  startDateLabel: PropTypes.string.isRequired,
  endDateLabel: PropTypes.string.isRequired,
  endDatePlaceholder: PropTypes.string,
  className: PropTypes.string,
};
